/**
 * SSC / TCS iON typing-test scoring.
 *
 * Net WPM = [(Keystrokes ÷ 5) − (Full Mistakes + Half Mistakes ÷ 2)] ÷ minutes
 *  - 5 keystrokes  = 1 word
 *  - Full mistake  = a wrong word left uncorrected
 *  - Half mistake  = a word typed correctly but only after a backspace correction
 *  - Omission      = a passage word that was never attempted
 */

export type WordState = 'correct' | 'full' | 'half' | 'omission';

export interface ExamResult {
  netWpm: number;
  grossWpm: number;
  keystrokes: number;
  accuracy: number;
  fullMistakes: number;
  halfMistakes: number;
  omissions: number;
  netPenalty: number;
  minutes: number;
  elapsedSec: number;
  correctWords: number;
  attemptedWords: number;
  wordStates: WordState[];
}

export function splitWords(text: string): string[] {
  const trimmed = text.trim();
  return trimmed.length ? trimmed.split(/\s+/) : [];
}

interface ScoreInput {
  sourceWords: string[];
  typed: string;
  keystrokes: number;
  elapsedSec: number;
  /** Indices of words the user corrected with backspace. */
  correctedIndices: Set<number>;
}

/** Per-word status for the passage (used for live coloring + results grid). */
export function computeWordStates(
  sourceWords: string[],
  typedWords: string[],
  correctedIndices: Set<number>
): WordState[] {
  return sourceWords.map((source, i) => {
    if (i >= typedWords.length) return 'omission';
    if (typedWords[i] === source) {
      return correctedIndices.has(i) ? 'half' : 'correct';
    }
    return 'full';
  });
}

export function scoreExam({
  sourceWords,
  typed,
  keystrokes,
  elapsedSec,
  correctedIndices,
}: ScoreInput): ExamResult {
  const typedWords = splitWords(typed);
  const wordStates = computeWordStates(sourceWords, typedWords, correctedIndices);

  let correctWords = 0;
  let fullMistakes = 0;
  let halfMistakes = 0;
  let omissions = 0;
  for (const state of wordStates) {
    if (state === 'correct') correctWords += 1;
    else if (state === 'full') fullMistakes += 1;
    else if (state === 'half') halfMistakes += 1;
    else omissions += 1;
  }

  const attemptedWords = typedWords.length;
  // Guard against divide-by-zero for instant submits.
  const minutes = Math.max(elapsedSec, 1) / 60;
  const grossWords = keystrokes / 5;
  const netPenalty = fullMistakes + halfMistakes * 0.5;
  const netWords = grossWords - netPenalty;

  const grossWpm = Math.max(0, Math.round(grossWords / minutes));
  const netWpm = Math.max(0, Math.round(netWords / minutes));
  const accuracy = attemptedWords ? Math.round((correctWords / attemptedWords) * 100) : 100;

  return {
    netWpm,
    grossWpm,
    keystrokes,
    accuracy,
    fullMistakes,
    halfMistakes,
    omissions,
    netPenalty,
    minutes: Math.round(minutes * 100) / 100,
    elapsedSec,
    correctWords,
    attemptedWords,
    wordStates,
  };
}
